/**
 * HeroBanner generates a component that uses a 16:4.5 image. The component generates a
 * MediaComponent and also generates a caption that overlaps. The caption can include a Title, text
 * and button. When in mobile the image becomes closer to a 16:9 aspect ratio.
 */

import React from 'react';
import '../styles/index.scss';
import BackgroundImage from 'gatsby-background-image';
import { Row, Col, Container } from 'react-bootstrap';
import { Media } from '../Media';
import { Button } from '../Button';
import VPLogo from '../assets/vineyard-partners-logo.svg';
const defaultProps = {
  bannerTitle: null,
  bannerText: null,
  brand: 'law',
  imageAlt: '',
  imageCaption: '',
  imageSrc: '',
  imageFluid: {},
  disabled: false,
  buttonLabel: '',
  buttonVariant: 'primary',
  onClick: () => {},
  children: null,
  variant: null,
  showLogo: false,
  anchorLink: null
};
/**
 * The HeroBanner uses a wide range of props to generate a customisable
 * Hero Banner that spans the width of the page.
 * @param  {RichText} bannerTitle - Will become a H2
 * @param  {RichText} bannerText
 * @param  {String} brand
 * @param  {String} imageAlt
 * @param  {String} imageCaption
 * @param  {String} imageSrc - If using an <img>
 * @param  {Object} imageFluid - Preprocessed imageset for background image
 * @param  {Boolean} disabled - Deems button unclickable
 * @param  {String} buttonLabel
 * @param  {String} buttonVariant - Determines primary or secondary
 * @param  {Function} onClick - Determines the function when the button is clicked
 * @param  {GatsbyImage} children - Use this to generate an optimised GatsbyImage
 * @param  {Number} variant - The type of hero banner to use
 * @param  {ReactNode} anchorLink - Optional gatsby anchor link
 */
const HeroBanner = _ref => {
  let {
    bannerTitle,
    bannerText,
    brand,
    imageAlt,
    imageCaption,
    imageSrc,
    imageFluid,
    disabled,
    buttonLabel,
    buttonVariant,
    onClick,
    children,
    variant,
    showLogo,
    anchorLink
  } = _ref;
  // Variant heading level 1 and 2
  switch (variant) {
    case 1:
    case 2:
      {
        const noLogoStyling = !showLogo ? 'justify-content-end' : '';
        return /*#__PURE__*/React.createElement(BackgroundImage, {
          className: "hero-banner-vineyard-image",
          fluid: imageFluid,
          alt: imageAlt
        }, /*#__PURE__*/React.createElement("div", {
          className: `${brand} px-0 hero-banner-vineyard-inner ${noLogoStyling}`
        }, showLogo && /*#__PURE__*/React.createElement("img", {
          "data-testid": "vp-logo",
          src: VPLogo,
          alt: "Vineyard Partners Logo"
        }), /*#__PURE__*/React.createElement("div", {
          className: "hero-banner-vineyard-info container"
        }, bannerTitle, bannerText)), /*#__PURE__*/React.createElement("div", {
          className: "black-opacity"
        }));
      }
    default:
      {
        const button = buttonLabel && /*#__PURE__*/React.createElement(Button, {
          size: "sm",
          brand: brand,
          label: buttonLabel,
          variant: buttonVariant,
          onClick: onClick,
          disabled: disabled
        });
        return /*#__PURE__*/React.createElement(Container, {
          fluid: true,
          className: `${brand} hero-banner`
        }, /*#__PURE__*/React.createElement(Row, {
          className: "pl-0 mt-3"
        }, /*#__PURE__*/React.createElement(Col, {
          className: "hero-banner-content",
          xs: 12,
          lg: 11
        }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
          className: "hero-banner-image-column px-0 pt-3 pr-lg-5",
          xs: 12,
          lg: {
            span: 11,
            offset: 1
          }
        }, children || /*#__PURE__*/React.createElement(Media, {
          alt: imageAlt,
          caption: imageCaption,
          src: imageSrc
        }))), /*#__PURE__*/React.createElement(Row, {
          className: "hero-banner-caption"
        }, /*#__PURE__*/React.createElement(Col, {
          xs: 11,
          lg: 10
        }, /*#__PURE__*/React.createElement(Row, {
          className: "hero-banner-caption-content pl-lg-4"
        }, /*#__PURE__*/React.createElement(Col, {
          className: "hero-banner-info pl-1 pl-lg-5 pt-4",
          xs: 11,
          lg: 8
        }, /*#__PURE__*/React.createElement(Row, {
          className: "hero-banner-title"
        }, /*#__PURE__*/React.createElement(Col, null, bannerTitle)), /*#__PURE__*/React.createElement(Row, {
          className: "hero-banner-text"
        }, /*#__PURE__*/React.createElement(Col, null, bannerText))), /*#__PURE__*/React.createElement(Col, {
          className: "hero-banner-button pl-0 ml-lg-5",
          lg: 3
        }, anchorLink || button))), /*#__PURE__*/React.createElement(Col, null)))));
      }
  }
};
HeroBanner.defaultProps = defaultProps;
export { HeroBanner };